import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from '../../../i18n/language-context';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { ChevronDown, Globe } from 'lucide-react';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { updateCurrentLanguage } from './services/language-service';
import { useConfig } from 'hooks/use-config';
import { useTheme } from 'components/core/theme-provider';
// import usFlag from 'assets/images/flags/us-flag.svg';
// import deFlag from 'assets/images/flags/de-flag.svg';

type LanguageSelectorVariant = 'default' | 'klax' | 'rail';

type LanguageSelectorProps = {
  variant?: LanguageSelectorVariant;
};

// const languageFlags: Record<string, string> = {
//   EN: usFlag,
//   DE: deFlag,
// };

const triggerClassNames: Record<LanguageSelectorVariant, string> = {
  default:
    'flex h-9 w-9 items-center gap-2 rounded-full border border-border px-1.5 text-foreground sm:h-9 sm:w-auto sm:px-3',
  klax: 'flex items-center justify-center gap-1 px-3 py-2',
  rail: 'flex h-4 items-center gap-1 rounded px-0 transition-colors hover:bg-grey-100 dark:hover:bg-sidebar-pill-dark',
};

type LanguageOption = { languageCode: string; languageName: string };

const baseOf = (code: string) => code.split('-')[0].toLowerCase();

/** Each language in its own words, the same in every UI language. */
export const NATIVE_LANGUAGE_NAMES: Record<string, string> = {
  'en-US': 'English',
  'de-DE': 'Deutsch (Deutschland)',
  'de-CH': 'Deutsch (Schweiz)',
  'fr-FR': 'Français (France)',
  'fr-CH': 'Français (Suisse)',
  'it-IT': 'Italiano (Italia)',
  'it-CH': 'Italiano (Svizzera)',
};

// Several variants of one language (fr-FR, fr-CH) would all read "FR", so a regional variant
// shows its region whenever another offered language shares the base. The variant named after
// its own language (de-DE, fr-FR) keeps the short code.
export const getLanguageShortLabel = (code: string, languages: LanguageOption[] = []) => {
  const [base, region] = code.split('-');
  const shared = languages.some((lang) => lang.languageCode !== code && baseOf(lang.languageCode) === baseOf(code));
  return shared && region && region.toLowerCase() !== base.toLowerCase()
    ? `${base.toUpperCase()}-${region.toUpperCase()}`
    : base.toUpperCase();
};

export const getLanguageMenuLabel = (lang: LanguageOption, languages: LanguageOption[] = []) => {
  const native = NATIVE_LANGUAGE_NAMES[lang.languageCode];
  if (native) return native;
  const [, region] = lang.languageCode.split('-');
  const shared = languages.some(
    (other) => other.languageCode !== lang.languageCode && baseOf(other.languageCode) === baseOf(lang.languageCode),
  );
  return shared && region && !lang.languageName.includes(`(${region.toUpperCase()})`)
    ? `${lang.languageName} (${region.toUpperCase()})`
    : lang.languageName;
};

function LanguageSelector({ variant = 'default' }: LanguageSelectorProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const user = useCommonStore((state) => state.user);
  const { currentLanguage, setLanguage, availableLanguages, isLoading } = useLanguageContext();
  const { configurationSettings } = useConfig();

  const changeLanguage = async (newLanguageCode: string) => {
    if (user) {
      updateCurrentLanguage(user.id, newLanguageCode, configurationSettings);
    }
    await setLanguage(newLanguageCode);
  };

  const languageCode = getLanguageShortLabel(currentLanguage, availableLanguages);
  // const languageFlag = languageFlags[languageCode];
  const isDark = theme === 'dark';

  const railTrigger = (
    <>
      {/* {languageFlag ? (
        <span className="flex shrink-0 items-center overflow-hidden rounded bg-true-white">
          <img src={languageFlag} alt={languageCode} width={16} height={12} className="block" />
        </span>
      ) : ( */}
      <span className={`text-2xs leading-none ${isDark ? 'text-true-white' : 'text-grey-900'}`}>
        {languageCode}
      </span>
      {/* )} */}
      <ChevronDown
        className={`size-2 shrink-0 ${isDark ? 'text-true-white' : 'text-grey-500'}`}
        strokeWidth={2.25}
        aria-hidden
      />
    </>
  );

  const klaxTrigger = (
    <>
      <span
        className={`text-sm font-normal leading-heading ${isDark ? 'text-true-white' : 'text-grey-950'}`}
      >
        {languageCode}
      </span>
      <ChevronDown
        className={`size-4 shrink-0 ${isDark ? 'text-true-white' : 'text-grey-950'}`}
        aria-hidden
      />
    </>
  );

  const defaultTrigger = (
    <>
      <Globe size={20} className="hidden text-sm sm:block" />
      <span className="text-sm font-medium sm:block">{languageCode}</span>
    </>
  );

  const triggerContent = { default: defaultTrigger, klax: klaxTrigger, rail: railTrigger }[variant];

  return (
    <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
      <DropdownMenuTrigger asChild className="cursor-pointer">
        <button
          type="button"
          className={triggerClassNames[variant]}
          aria-label={variant === 'rail' ? t('LANGUAGE') || 'Language' : undefined}
        >
          {triggerContent}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align={variant === 'rail' ? 'start' : 'end'}
        side={variant === 'rail' ? 'right' : undefined}
      >
        {isLoading ? (
          <DropdownMenuItem disabled>{t('LOADING_LANGUAGES')}...</DropdownMenuItem>
        ) : !availableLanguages || availableLanguages.length === 0 ? (
          <DropdownMenuItem disabled>{t('PLEASE_LOGIN_FIRST_ACCESS_LANGUAGES')}</DropdownMenuItem>
        ) : availableLanguages && availableLanguages.length > 0 ? (
          availableLanguages.map((lang, i) => (
            <div key={lang.itemId}>
              <DropdownMenuItem
                lang={lang.languageCode}
                className={`${lang.languageCode === currentLanguage ? 'font-bold cursor-pointer' : ''}`}
                onClick={() => changeLanguage(lang.languageCode)}
              >
                {NATIVE_LANGUAGE_NAMES[lang.languageCode] ??
                  t(`LANGUAGE_${lang.languageCode.replace('-', '_').toUpperCase()}`, {
                    defaultValue: getLanguageMenuLabel(lang, availableLanguages),
                  })}
              </DropdownMenuItem>
              {i !== availableLanguages.length - 1 && <DropdownMenuSeparator />}
            </div>
          ))
        ) : (
          <DropdownMenuItem disabled>{t('NO_LANGUAGES_AVAILABLE')}</DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LanguageSelector;
